/**
 * `<SavedSearchesPanel>` — the saved-search list + jump (frontend.md §2.1, §2.4).
 *
 * UX-reconciliation rip-and-replace: rebuilt from `docs/refactor/_extracted-standalone-source/
 * 05_2cdfc807.jsx` lines 342–366 (the standalone's `SavedSearches`). A `.label-mono`
 * heading sits above a responsive grid of `.res-card`s — one per saved search — plus a
 * trailing dashed `.res-card` "new saved search" affordance. Each card shows a star, the
 * name, and a mono `count · time` foot line.
 *
 * A saved search is a serialized search URL (frontend.md §2.2, §3.2). This panel lists
 * `GET /api/v1/saved_searches` and, on click, navigates to the stored URL state.
 *
 * BACKEND SEAM (honest UI, not a broken button): the v1 backend `GET /saved_searches`
 * returns an empty list (`rest_app.py`: no `SavedSearchRepo` in the v1 api-core port set;
 * the integration layer threads it in) and there is NO `POST`/`DELETE` route wired yet.
 * So the "+ New saved search" affordance renders as a disabled `.res-card--ghost` with a
 * tooltip explaining the seam, rather than a button that would 404. When the backend adds
 * the write routes, the save affordance + a `useSaveSearchMutation` slot in.
 */

import type { SavedSearch } from '@/types';
import { useSavedSearches } from '@/hooks';
import { Icon, LoadingState } from '@/components';

interface SavedSearchesPanelProps {
  /** Navigate to a saved search's serialized URL state. */
  onJump: (search: SavedSearch) => void;
}

function formatRelative(lastRunAt: string | null): string {
  if (lastRunAt === null) return 'never run';
  const then = new Date(lastRunAt).getTime();
  if (Number.isNaN(then)) return 'unknown';
  const diffMs = Date.now() - then;
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.round(days / 30);
  return `${months}mo ago`;
}

export function SavedSearchesPanel({ onJump }: SavedSearchesPanelProps): JSX.Element {
  const savedQuery = useSavedSearches();
  const items = savedQuery.data?.items ?? [];

  return (
    <div className="se-saved-section">
      <div className="label-mono">Saved searches</div>

      {savedQuery.isLoading && <LoadingState variant="inline" label="Loading…" />}

      <div className="se-saved">
        {items.map((search) => {
          const count =
            typeof search.query['count'] === 'number'
              ? (search.query['count'] as number)
              : null;
          return (
            <button
              key={search.id}
              type="button"
              className="res-card se-saved-card"
              onClick={() => onJump(search)}
              title={`Open saved search “${search.name}”`}
            >
              <div className="se-saved-card__head">
                <Icon
                  name="star"
                  size={13}
                  style={{
                    color: 'var(--accent-amber)',
                    fill: search.isPinned ? 'var(--accent-amber)' : 'transparent',
                  }}
                />
                <span className="se-saved-card__name">{search.name}</span>
              </div>
              <div className="se-saved-card__foot">
                {count !== null && (
                  <>
                    <span>{count} results</span>
                    <span aria-hidden="true">·</span>
                  </>
                )}
                <span>{formatRelative(search.lastRunAt)}</span>
              </div>
            </button>
          );
        })}

        {/* Honest backend seam: the disabled ghost slot acknowledges the affordance the
            standalone shows ("+ New saved search") without wiring a button that would 404
            against the v1 backend. The tooltip names the integration seam. */}
        <div
          className="res-card se-saved-card se-saved-card--ghost"
          title="Saved-search persistence lands at backend integration (no SavedSearchRepo in the v1 api-core port set)"
          aria-disabled="true"
        >
          <Icon name="plus" size={14} />
          <span>New saved search</span>
        </div>
      </div>
    </div>
  );
}
